import { useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { useTranslation } from "@motolynk/i18n";
import { imageUrl, MediaPickerDialog } from "@motolynk/module-medialib";
import { Alert, Badge, Button, EmptyState, Icon, MediaThumb } from "@motolynk/ui";

import type { EditorValues } from "./values";

/** The product's photos from the media library (public files of the vendor, or platform files for our store). */
export function PhotosSection({ disabled }: { disabled?: boolean }) {
  const { t } = useTranslation("products");
  const { control, setValue, formState } = useFormContext<EditorValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "media", keyName: "key" });
  const media = useWatch({ control, name: "media" }) ?? [];
  const [picking, setPicking] = useState(false);
  const errors = formState.errors.media as
    | (Record<number, { message?: string }> & { message?: string; root?: { message?: string } })
    | undefined;
  const messages = [
    errors?.message,
    errors?.root?.message,
    ...fields.map((_, index) => errors?.[index]?.message),
  ].filter(Boolean);

  const makePrimary = (index: number) =>
    media.forEach((_, i) => setValue(`media.${i}.is_primary`, i === index, { shouldDirty: true }));
  const primaryIndex = Math.max(
    media.findIndex((row) => row.is_primary),
    0,
  );

  return (
    <div className="space-y-4">
      {messages.length > 0 && <Alert tone="danger">{messages[0]}</Alert>}
      {fields.length === 0 ? (
        <EmptyState icon="image" title={t("editor.photos.empty")} />
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {fields.map((row, index) => (
            <li key={row.key} className="space-y-2 rounded-card border border-border p-2">
              <MediaThumb
                className="rounded-lg"
                item={{
                  kind: "image",
                  thumbnailUrl: media[index]?.url ?? null,
                  title: media[index]?.name ?? "",
                }}
              />
              <p className="truncate text-xs text-muted-foreground">{media[index]?.name}</p>
              <div className="flex flex-wrap items-center gap-1">
                {index === primaryIndex ? (
                  <Badge tone="brand">{t("editor.photos.primary")}</Badge>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={disabled}
                    onClick={() => makePrimary(index)}
                  >
                    {t("editor.photos.makePrimary")}
                  </Button>
                )}
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  disabled={disabled}
                  aria-label={`${t("editor.photos.remove")}: ${media[index]?.name ?? ""}`}
                  onClick={() => remove(index)}
                >
                  <Icon name="trash" size={16} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Button type="button" variant="outline" disabled={disabled} onClick={() => setPicking(true)}>
        <Icon name="image" size={16} />
        {t("editor.photos.add")}
      </Button>
      <MediaPickerDialog
        open={picking}
        onOpenChange={setPicking}
        multiple
        kind="image"
        storefront
        initial={media.map((row) => row.media)}
        onPick={(picked) => {
          const known = new Set(media.map((row) => row.media));
          const keep = new Set(picked.map((item) => item.uid));
          // unticked in the picker = removed from the gallery
          [...media].reverse().forEach((row, reverseIndex) => {
            if (!keep.has(row.media)) remove(media.length - 1 - reverseIndex);
          });
          for (const item of picked) {
            if (known.has(item.uid)) continue;
            append({
              media: item.uid,
              url: imageUrl(item, "small"),
              name: item.title || item.original_name || item.uid,
              is_primary: false,
            });
          }
        }}
      />
    </div>
  );
}
